import { monthsUntil, monthlyNeeded, fvLump, fvSeries, extraMortgagePayment, cents } from './format';
import { IRS_IRA_LIMIT } from './constants';

/** Sum of Cash + Other Liquid Assets account balances */
export function computeLiquidTotal(accounts) {
  return (accounts || [])
    .filter(a => a.name === 'Cash' || a.name === 'Other Liquid Assets')
    .reduce((s, a) => s + (a.balance || 0), 0);
}

/**
 * This month's net movement on an account (Deposits add, Debits subtract).
 * A movement, not a balance — the ledger only holds the current month's rows.
 */
export function accountLedgerBalance(transactions, accountName) {
  const raw = (transactions || [])
    .filter(t => t.accountName === accountName)
    .reduce((s, t) => s + (t.type === 'Deposit' ? (t.amount || 0) : -(t.amount || 0)), 0);
  return Math.round(raw * 100) / 100 || 0;
}

/**
 * What an account is worth right now — the single definition every tab uses, so
 * a row and the subtotal above it can't disagree.
 *
 * A Live account is its opening balance (carried from last month's close) plus
 * this month's movement. Without the opening balance it would read as just this
 * month's flow, which is why Live balances used to reset to zero on the 1st.
 * A Manual account is simply whatever was typed in.
 */
export function accountBalance(account, transactions) {
  if (!account) return 0;
  if (!account.ledgerDriven) return account.balance || 0;
  const raw = (account.openingBalance || 0) + accountLedgerBalance(transactions, account.name);
  return Math.round(raw * 100) / 100 || 0;
}

/**
 * Emergency fund total on the Accounts tab — the sum of accounts flagged as
 * emergency savings (cash, savings, a brokerage emergency fund, …).
 *
 * Account-based on purpose, matching the spreadsheet's invested-emergency line,
 * which adds those account balances. It used to add the Emergency Fund
 * *envelope* on top, which double-counts: money grabbed into the envelope is
 * usually sitting in one of the flagged accounts already. The envelope is still
 * a normal envelope for budgeting toward the fund; it just isn't added here.
 */
export function computeEmergencyFund(categories, accounts, transactions) {
  return computeInvestedLiquidTotal(accounts, transactions);
}

/**
 * What the Emergency Fund *envelope* adds on top of Grabbed − Spent.
 *
 * The spreadsheet's Available (Budget!E9) is Assigned plus the Schwab-style
 * tracking investment only — not the bank/cash accounts that already hold
 * money grabbed into the envelope. Those Budget-type accounts still count in
 * {@link computeEmergencyFund}; they just aren't added here a second time.
 */
export function computeEmergencyFundEnvelopeBonus(accounts, transactions) {
  return sumInvestedLiquid(accounts, transactions, a => a.accountType !== 'Budget');
}

/**
 * Sum of all invested-liquid accounts.
 *
 * Each account is valued the same way the Accounts tab displays it: from the
 * transaction ledger when it's marked Live, otherwise from its stored balance.
 * Valuing these ledger-only regardless of the flag would make the Emergency
 * Fund total disagree with the row the user is looking at.
 * Negative balances are floored at 0 — a liability doesn't reduce the fund.
 */
export function computeInvestedLiquidTotal(accounts, transactions) {
  return sumInvestedLiquid(accounts, transactions, () => true);
}

function sumInvestedLiquid(accounts, transactions, extra) {
  return (accounts || [])
    .filter(a => a.isInvestedLiquid && extra(a))
    .reduce((s, a) => {
      const bal = accountBalance(a, transactions);
      return s + (bal > 0 ? bal : 0);
    }, 0);
}

/**
 * Up for Grabs (income waiting to be budgeted):
 *   = Assign-category deposits
 *   + Cash / Other Liquid (manual)
 *   + Live Budget opening balances (Checking, savings, and card *debt*)
 *   − total category assigned
 *
 * Excel tags each register's starting-balance row Assign, then subtracts the
 * card's opening debit. Trigger stores those as opening_balance instead of
 * ledger rows, so they have to enter RTA here or the money is invisible —
 * grabbing it into Emergency Fund then looks like a fabricated deficit.
 * Credit-card openings are negative, same as Excel's −Amex!C2.
 *
 * A *positive* leftover on a loan or card register is payoff sitting on a
 * liability, not cash to assign. Checking and savings openings still count;
 * a card's starting debt (negative) still reduces the pot.
 */
export function isLiabilityAccount(account, categories) {
  if (!account) return false;
  if (account.accountType === 'Loan' || account.isCreditCard) return true;
  return (categories || []).some(c => c.groupName === 'Credit Card' && c.name === account.name);
}

export function computeLiveOpeningTotal(accounts, categories) {
  return (accounts || [])
    .filter(a => a.ledgerDriven && a.accountType === 'Budget')
    .reduce((s, a) => {
      const open = a.openingBalance || 0;
      if (isLiabilityAccount(a, categories) && open > 0) return s;
      return s + open;
    }, 0);
}

export function computeRTA(transactions, categories, accounts) {
  const assignDeposits = (transactions || [])
    .filter(t => t.categoryName === 'Up for Grabs' && t.type === 'Deposit')
    .reduce((s, t) => s + (t.amount || 0), 0);
  const liquidTotal = computeLiquidTotal(accounts);
  const openings = computeLiveOpeningTotal(accounts, categories);
  // Round each term, not just the answer: summing dozens of raw doubles can
  // drift past half a cent on its own, which is how Up for Grabs reported
  // leftover money when every envelope was actually full.
  const totalAssigned = (categories || [])
    .reduce((s, c) => s + cents(c.assigned), 0);
  return cents(cents(assignDeposits) + cents(liquidTotal) + cents(openings) - totalAssigned);
}

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// Generalized strategy engine â€” operates on dynamic member/plan lists.
//
// Plan shapes (mirror the backend entities):
//   member:      { id, name, monthlyGrossIncome, deductionPct, retirementDate }
//   savingsPlan: { id, name, type: 'RETIREMENT_401K'|'IRA'|'BROKERAGE'|'COLLEGE'|'OTHER',
//                  ownerMemberId, balance, annualRate, yearlyMax,
//                  targetAmount, targetGoalPct, targetDate, enabled }
//   debtPlan:    { id, name, type: 'MORTGAGE'|'STUDENT_LOAN'|'OTHER', principal,
//                  annualRate, baseMonthlyPmt, escrow, payoffGoalDate, enabled,
//                  toggleFullPayment }
//   settings:    { sharedRetirementGoal, iraGoalSharePct, max401kIncomePct }
//   income:      { memberId|null, label, amount }   (one month's income rows)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

/**
 * Raw calculator targets per plan â€” no toggle logic; redistribution is
 * handled by computeEffectiveSavings.
 *
 * Returns {
 *   savingsTargets: { [planId]: { monthlyTarget, months, fv, fvNeeded?, pctOfOwnerIncome } },
 *   debtTargets:    { [planId]: { extraGoal, baseMo, totalMo, months } },
 *   monthsByMemberId, totalSavingsMo, k401kFVNeeded, retGoal, totalIncome,
 * }
 */
export function computeRetirementTargets({ members = [], savingsPlans = [], debtPlans = [], settings = {}, incomes = [] }) {
  const retGoal  = settings.sharedRetirementGoal ?? 2_000_000;
  const iraShare = settings.iraGoalSharePct ?? 0.145;

  const totalIncome = incomes.reduce((s, i) => s + (i.amount || 0), 0);
  const incomeByMemberId = {};
  for (const i of incomes) {
    if (i.memberId != null)
      incomeByMemberId[i.memberId] = (incomeByMemberId[i.memberId] || 0) + (i.amount || 0);
  }

  const monthsByMemberId = {};
  for (const m of members) monthsByMemberId[m.id] = monthsUntil(m.retirementDate);
  const maxMemberMonths = Math.max(0, ...members.map(m => monthsByMemberId[m.id]));

  // Retirement-goal share: equal split among members with a retirement date,
  // falling back to all members so targets stay meaningful before dates are set.
  const dated = members.filter(m => m.retirementDate);
  const shareMembers = dated.length > 0 ? dated : members;
  const shareByMemberId = {};
  for (const m of shareMembers) shareByMemberId[m.id] = 1 / shareMembers.length;

  // Horizon: owner's months to retirement; ownerless plans use the household's
  // longest horizon (e.g. a shared brokerage grows until the last retirement).
  const horizon = (p) => p.ownerMemberId != null
    ? (monthsByMemberId[p.ownerMemberId] ?? 0)
    : maxMemberMonths;

  // IRA + brokerage balance-only FVs reduce what the 401ks must cover.
  // (Future IRA/brokerage CONTRIBUTIONS are separate goals and must not crowd
  // out 401k targets â€” otherwise the 401k values collapse to $0.)
  let iraAndBrokBalFV = 0;
  for (const p of savingsPlans) {
    if (p.type === 'IRA' || p.type === 'BROKERAGE')
      iraAndBrokBalFV += fvLump(p.balance || 0, p.annualRate ?? 0.07, horizon(p));
  }
  const k401kFVNeeded = Math.max(0, retGoal - iraAndBrokBalFV);

  // A member's retirement share splits across their 401k plans if they have several.
  const k401kCountByMember = {};
  for (const p of savingsPlans) {
    if (p.type === 'RETIREMENT_401K' && p.ownerMemberId != null)
      k401kCountByMember[p.ownerMemberId] = (k401kCountByMember[p.ownerMemberId] || 0) + 1;
  }

  const savingsTargets = {};
  for (const p of savingsPlans) {
    const rate   = p.annualRate ?? 0.07;
    const months = p.type === 'COLLEGE' || p.type === 'OTHER'
      ? monthsUntil(p.targetDate)
      : horizon(p);

    let monthlyTarget = 0;
    let fvNeeded;
    if (p.type === 'IRA') {
      const maxMo = (p.yearlyMax ?? IRS_IRA_LIMIT) / 12;
      monthlyTarget = Math.min(maxMo, monthlyNeeded(retGoal * iraShare, p.balance || 0, rate, months));
    } else if (p.type === 'BROKERAGE') {
      // Brokerage fills a share of the shared retirement goal over the owner's
      // horizon — like the IRA, but with no IRS cap. Share defaults to the IRA share.
      const share = p.targetGoalPct ?? iraShare;
      fvNeeded = retGoal * share;
      monthlyTarget = monthlyNeeded(fvNeeded, p.balance || 0, rate, months);
    } else if (p.type === 'RETIREMENT_401K') {
      const share = (shareByMemberId[p.ownerMemberId] ?? 0) / (k401kCountByMember[p.ownerMemberId] || 1);
      fvNeeded = k401kFVNeeded * share;
      monthlyTarget = monthlyNeeded(fvNeeded, p.balance || 0, rate, months);
    } else { // COLLEGE / OTHER: fixed-target pattern
      const goal = (p.targetAmount ?? 0) * (p.targetGoalPct ?? 1);
      monthlyTarget = goal > 0 ? monthlyNeeded(goal, p.balance || 0, rate, months) : 0;
    }

    const ownerIncome = p.ownerMemberId != null ? (incomeByMemberId[p.ownerMemberId] || 0) : 0;
    savingsTargets[p.id] = {
      monthlyTarget,
      months,
      fv: fvLump(p.balance || 0, rate, months) + fvSeries(monthlyTarget, rate, months),
      fvNeeded,
      pctOfOwnerIncome: ownerIncome > 0 ? monthlyTarget / ownerIncome : 0,
    };
  }

  const debtTargets = {};
  for (const p of debtPlans) {
    const months    = monthsUntil(p.payoffGoalDate);
    const extraGoal = extraMortgagePayment(
      p.principal || 0, p.annualRate || 0, p.baseMonthlyPmt || 0, p.escrow ?? 0, months);
    debtTargets[p.id] = {
      extraGoal,
      baseMo: p.baseMonthlyPmt || 0,
      totalMo: (p.baseMonthlyPmt || 0) + extraGoal,
      months,
    };
  }

  return {
    savingsTargets, debtTargets, monthsByMemberId,
    totalSavingsMo: Object.values(savingsTargets).reduce((s, t) => s + t.monthlyTarget, 0),
    k401kFVNeeded, retGoal, totalIncome,
  };
}

/** Monthly savings Goal is the Setup calculator — never a Strategy-typed override. */
export function savingsPlanMonthlyTarget(plan, calcTargets) {
  return calcTargets?.savingsTargets?.[plan?.id]?.monthlyTarget || 0;
}

/**
 * Toggle redistribution + cascade over dynamic plan lists.
 *
 * @param savingsPlans      [{ id, type, ownerMemberId, enabled, baseAmount }]
 * @param debtPlans         [{ id, enabled, extraGoal }] â€” extraGoal is the plan's
 *                          toggleable amount (full payment for toggleFullPayment
 *                          plans, payoff-extra only for mortgage-style plans)
 * @param incomeByMemberId  { [memberId]: grossMonthly } â€” drives 401k caps
 * @param varBaseTotal      sum of variable spending goals (before scale)
 * @param max401kIncomePct  per-member 401k cap as share of gross income
 * @returns { effSavingsByPlanId, effDebtExtraByPlanId, varScale }
 *
 * Disabled plans contribute nothing and redistribute nothing — off means off.
 * The only money that moves between plans is 401k cap overflow, because a cap
 * blocks a contribution the user still intends to make.
 *
 * Cascade order:
 *  1. per-plan 401k income caps within a type group; capped excess â†’ peers with room
 *  2. remaining cap overflow â†’ active savings proportionally; caps re-enforced;
 *     post-cap excess â†’ non-401k savings
 *  3. residual cap overflow â†’ active debt extras â†’ variable
 */
export function computeEffectiveSavings({
  savingsPlans = [], debtPlans = [], incomeByMemberId = {},
  varBaseTotal = 0, max401kIncomePct = 0.80,
}) {
  const eff = {};

  const capOf = (p) => {
    if (p.type !== 'RETIREMENT_401K' || !p.enabled) return Infinity;
    const inc = p.ownerMemberId != null ? (incomeByMemberId[p.ownerMemberId] || 0) : 0;
    return inc > 0 ? inc * max401kIncomePct : Infinity;
  };

  // Step 1 â€” within-type-group redistribution (+ 401k caps with peer absorption)
  const byType = {};
  for (const p of savingsPlans) (byType[p.type] = byType[p.type] || []).push(p);

  let crossSavingsOverflow = 0;
  for (const group of Object.values(byType)) {
    const enabled = group.filter(p => p.enabled);
    for (const p of group) if (!p.enabled) eff[p.id] = 0;

    // Off means off: a disabled plan contributes nothing and its money is not
    // handed to anything else. It used to be shared out among enabled peers,
    // which meant switching five plans off silently tripled the sixth — the
    // single largest gap between this engine and the spreadsheet.
    if (enabled.length === 0) continue;

    const wanted = {};
    for (const p of enabled) wanted[p.id] = p.baseAmount || 0;

    // Apply caps; excess flows into peers' remaining room (no-op when caps are
    // Infinity). A cap is not a toggle — money the IRS/plan limit blocks still
    // has to go somewhere, so this redistribution stays.
    let excess = 0;
    for (const p of enabled) {
      eff[p.id] = Math.min(capOf(p), wanted[p.id]);
      excess += wanted[p.id] - eff[p.id];
    }
    let guard = 0;
    while (excess > 0.005 && guard++ < 10) {
      const withRoom = enabled.filter(p => capOf(p) - eff[p.id] > 0.005);
      if (withRoom.length === 0) break;
      const totalRoom = withRoom.reduce((s, p) => s + (capOf(p) - eff[p.id]), 0);
      const dist = Math.min(excess, totalRoom);
      for (const p of withRoom) eff[p.id] += dist * (capOf(p) - eff[p.id]) / totalRoom;
      excess -= dist;
    }
    crossSavingsOverflow += Math.max(0, excess);
  }

  // Step 2 â€” cross-savings overflow distributed proportionally to active savings
  const activeSavTotal = savingsPlans.reduce((s, p) => s + eff[p.id], 0);
  if (crossSavingsOverflow > 0 && activeSavTotal > 0) {
    for (const p of savingsPlans) {
      if (eff[p.id] > 0) eff[p.id] += crossSavingsOverflow * (eff[p.id] / activeSavTotal);
    }
  }

  // Step 2b â€” re-enforce 401k caps after cross-savings distribution.
  // Excess goes to non-401k savings only; 401k never receives secondary overflow.
  let postCapOverflow = 0;
  for (const p of savingsPlans) {
    if (p.type === 'RETIREMENT_401K') {
      const cap = capOf(p);
      if (eff[p.id] > cap + 0.005) {
        postCapOverflow += eff[p.id] - cap;
        eff[p.id] = cap;
      }
    }
  }
  if (postCapOverflow > 0.01) {
    const non401k = savingsPlans.filter(p => p.type !== 'RETIREMENT_401K' && eff[p.id] > 0);
    const nTotal = non401k.reduce((s, p) => s + eff[p.id], 0);
    if (nTotal > 0.01) {
      for (const p of non401k) eff[p.id] += postCapOverflow * (eff[p.id] / nTotal);
      postCapOverflow = 0; // fully absorbed by non-401k savings
    }
    // If postCapOverflow > 0 after this, Step 3 cascades it to debt then variable
  }

  // Step 3 â€” Savings â†” Debt â†” Variable cascade
  const totalEffSavings  = savingsPlans.reduce((s, p) => s + eff[p.id], 0);
  const allSavingsEffOff = totalEffSavings < 0.01;

  const effDebt = {};
  for (const d of debtPlans) effDebt[d.id] = d.enabled ? (d.extraGoal || 0) : 0;
  let varOverflow = 0;

  const activeDebtBase = debtPlans.reduce((s, d) => s + (d.enabled  ? (d.extraGoal || 0) : 0), 0);

  // A disabled debt's extra is simply not paid — same rule as savings. Its base
  // payment still counts, but as a fixed cost in the Strategy tab, not here.
  // Only genuine 401k cap overflow cascades onward, below.
  if (allSavingsEffOff && crossSavingsOverflow > 0.01) {
    if (activeDebtBase > 0) {
      for (const d of debtPlans) {
        if (d.enabled) effDebt[d.id] += crossSavingsOverflow * ((d.extraGoal || 0) / activeDebtBase);
      }
    } else {
      varOverflow = crossSavingsOverflow;
    }
  }

  // Final cascade for any 401k cap overflow savings couldn't absorb
  if (postCapOverflow > 0.01) {
    if (activeDebtBase > 0) {
      for (const d of debtPlans) {
        if (d.enabled) effDebt[d.id] += postCapOverflow * ((d.extraGoal || 0) / activeDebtBase);
      }
    } else {
      varOverflow += postCapOverflow;
    }
  }

  const varScale = varBaseTotal > 0 && varOverflow > 0
    ? (varBaseTotal + varOverflow) / varBaseTotal : 1;

  return { effSavingsByPlanId: eff, effDebtExtraByPlanId: effDebt, varScale };
}

/**
 * Variable / debt / savings cut calculation over dynamic lists.
 *
 * @param incomes        [{ memberId|null, amount }] â€” month income rows
 * @param members        [{ id, deductionPct }]
 * @param fixedTotal     fixed costs (no cut applies)
 * @param spendingGoals  [{ id, amount }]
 * @param savingsAmounts [{ id, amount }] â€” effective savings (post-redistribution)
 * @param debtExtras     [{ id, amount }] â€” effective debt extras (post-redistribution)
 */
export function computeStrategy({
  incomes = [], members = [], fixedTotal = 0,
  spendingGoals = [], savingsAmounts = [], debtExtras = [],
}) {
  const dedPctByMember = {};
  for (const m of members) dedPctByMember[m.id] = m.deductionPct || 0;

  let grossIncome = 0, totalDed = 0;
  const dedByMemberId = {};
  for (const i of incomes) {
    grossIncome += i.amount || 0;
    if (i.memberId != null) {
      const d = (i.amount || 0) * (dedPctByMember[i.memberId] || 0);
      dedByMemberId[i.memberId] = (dedByMemberId[i.memberId] || 0) + d;
      totalDed += d;
    }
  }
  const netIncome = grossIncome - totalDed;

  const variableGoalSum = spendingGoals.reduce((s, g) => s + (g.amount || 0), 0);
  const debtGoalSum     = debtExtras.reduce((s, d) => s + (d.amount || 0), 0);
  const savingsGoalSum  = savingsAmounts.reduce((s, p) => s + (p.amount || 0), 0);

  const totalGoal = fixedTotal + variableGoalSum + debtGoalSum + savingsGoalSum;
  const deficit   = Math.max(0, totalGoal - netIncome);
  const surplus   = Math.max(0, netIncome - totalGoal);

  let variableCut = 0, debtCut = 0, savingsCut = 0;
  if (deficit > 0) {
    // First pass: apply 65 / 16 / 19 split, each capped at the category total
    let varTaken  = Math.min(variableGoalSum, deficit * 0.65);
    let debtTaken = Math.min(debtGoalSum,     deficit * 0.16);
    let savTaken  = Math.min(savingsGoalSum,  deficit * 0.19);

    // Second pass: any unabsorbed deficit (from zeroed/capped categories) is
    // redistributed proportionally to categories that still have remaining room.
    const leftover = deficit - varTaken - debtTaken - savTaken;
    if (leftover > 0.01) {
      const varRoom   = variableGoalSum - varTaken;
      const debtRoom  = debtGoalSum     - debtTaken;
      const savRoom   = savingsGoalSum  - savTaken;
      const totalRoom = varRoom + debtRoom + savRoom;
      if (totalRoom > 0.01) {
        varTaken  += leftover * varRoom  / totalRoom;
        debtTaken += leftover * debtRoom / totalRoom;
        savTaken  += leftover * savRoom  / totalRoom;
      }
      // If totalRoom = 0, fixed costs alone exceed income â€” nothing more to cut
    }

    variableCut = variableGoalSum > 0 ? Math.min(1, varTaken  / variableGoalSum) : 0;
    debtCut     = debtGoalSum     > 0 ? Math.min(1, debtTaken / debtGoalSum)     : 0;
    savingsCut  = savingsGoalSum  > 0 ? Math.min(1, savTaken  / savingsGoalSum)  : 0;
  }

  // ── Surplus: the other half of the rule ────────────────────────────────────
  //
  // When income covers every goal, the leftover used to sit unallocated, so the
  // Balance line showed a gap and toggling a plan on changed nothing. Surplus is
  // now given out the way the plan describes: 65% to debt payoff, 19% to
  // savings, 16% to variable — the mirror of the cut split, weighted toward
  // clearing debt.
  //
  // An arm with no active goal can't take its share (there's nothing to scale),
  // so it passes to the arms that can. If nothing at all is active the surplus
  // stays in `balance`, which is honest: there is no goal to put it against.
  if (surplus > 0.005) {
    const shares = { variable: 0.16, debt: 0.65, savings: 0.19 };
    const base   = { variable: variableGoalSum, debt: debtGoalSum, savings: savingsGoalSum };
    const able   = Object.keys(shares).filter(k => base[k] > 0.005);

    if (able.length > 0) {
      const claimed = able.reduce((s, k) => s + shares[k], 0);
      const given   = {};
      // Re-weight among the arms that can absorb, so the full surplus lands.
      for (const k of able) given[k] = surplus * (shares[k] / claimed);

      // Negative "cut" = addition, so every downstream `(1 - cut)` already works.
      if (given.variable) variableCut = -given.variable / variableGoalSum;
      if (given.debt)     debtCut     = -given.debt     / debtGoalSum;
      if (given.savings)  savingsCut  = -given.savings  / savingsGoalSum;
    }
  }

  const variableAdjustedById = {};
  for (const g of spendingGoals) variableAdjustedById[g.id] = (g.amount || 0) * (1 - variableCut);
  const debtAdjustedById = {};
  for (const d of debtExtras) debtAdjustedById[d.id] = (d.amount || 0) * (1 - debtCut);
  const savingsAdjustedById = {};
  for (const p of savingsAmounts) savingsAdjustedById[p.id] = (p.amount || 0) * (1 - savingsCut);

  const balance = netIncome - fixedTotal
    - variableGoalSum * (1 - variableCut)
    - debtGoalSum     * (1 - debtCut)
    - savingsGoalSum  * (1 - savingsCut);

  return {
    grossIncome, netIncome, totalDed, dedByMemberId,
    variableGoalSum, debtGoalSum, savingsGoalSum,
    totalGoal, deficit, surplus,
    variableCut, debtCut, savingsCut,
    variableAdjustedById, debtAdjustedById, savingsAdjustedById,
    balance,
  };
}
